import { useMemo, useState } from "react";

import clsx from "clsx";
import {
  Controller,
  type FieldArrayWithId,
  type UseFieldArrayRemove,
  type UseFieldArrayUpdate,
  type UseFormReturn,
} from "react-hook-form";

import type z from "zod";

import { Tooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { LightSwitch } from "@components/utilities";
import { NotificationType } from "@features/mmm/components/notifications/grouped-notifications";
import { Notification } from "@features/mmm/components/notifications/notification";
import { getCurrencySymbol } from "@features/mmm/utils/formatters";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { formatCurrency } from "@north-beam/nb-common/formatting";
import { TertiaryButton } from "@shared/buttons";
import { CurrencyInput } from "@shared/currency-input";
import { TextInput } from "@shared/text-input";

import { type DataColumn, type SpendConstraint } from "../types";

import { type ClientSettingSchema } from "./client-setting-form";
import { SpendConstraintLabel } from "./spend-constraint-label";

type ClientSetting = z.infer<typeof ClientSettingSchema>;

type SpendConstraintFormComponentProps = {
  spendConstraintField: FieldArrayWithId<ClientSetting, "spend_constraints">;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<ClientSetting, "spend_constraints">;
  form: UseFormReturn<z.infer<typeof ClientSettingSchema>>;
  dataColumnsMap: Map<string, DataColumn>;
  index: number;
  periodLengthDays: number;
  minBudgetValue: number | null;
  maxBudgetValue: number | null;
  hasError: boolean;
  existingSpendConstraints: SpendConstraint[];
  previousPeriodSpend?: number;
};

export function SpendConstraintFormComponent({
  spendConstraintField,
  remove,
  update,
  form,
  dataColumnsMap,
  index,
  periodLengthDays,
  minBudgetValue,
  maxBudgetValue,
  hasError,
  existingSpendConstraints,
  previousPeriodSpend,
}: SpendConstraintFormComponentProps) {
  const { user } = useUser();
  const [isOff, setIsOff] = useState(false);

  const isUnavailable = useMemo(() => {
    return !dataColumnsMap.get(spendConstraintField.data_column_id);
  }, [dataColumnsMap, spendConstraintField.data_column_id]);

  const getDailySpendFromPeriodSpend = (spendAmount: number) => {
    const val = Math.round((100 * spendAmount) / periodLengthDays) / 100;
    if (Number.isNaN(val) || !Number.isFinite(val)) {
      return "Invalid";
      //  return "∞";
    }
    return formatCurrency(val, {
      code: user.currencies.currentCurrency,
      precision: "exact",
    });
  };

  const getDailySpendText = (
    spendAmount: number | null | undefined,
    isMax: boolean,
  ) => {
    if (spendAmount === null || spendAmount === undefined) {
      return isMax
        ? `${getCurrencySymbol(user.currencies.currentCurrency)} ∞`
        : `${getCurrencySymbol(user.currencies.currentCurrency)} 0`;
    }

    return getDailySpendFromPeriodSpend(spendAmount);
  };

  const constructUnknownDataColumn = (
    dataColumnPartial: Partial<DataColumn>,
  ): DataColumn => {
    return {
      id: dataColumnPartial.id ?? "-1",
      is_ready_for_use_in_model:
        dataColumnPartial.is_ready_for_use_in_model ?? false,
      last_synchronized: dataColumnPartial.last_synchronized ?? "",
      friendly_name: dataColumnPartial.friendly_name ?? "Unknown constraint",
      column_name: dataColumnPartial.column_name ?? "Umknown constraint",
      is_from_mta: dataColumnPartial.is_from_mta ?? false,
      is_present: dataColumnPartial.is_present ?? false,
      is_from_mmm_custom_data:
        dataColumnPartial.is_from_mmm_custom_data ?? false,
      is_target: dataColumnPartial.is_target ?? false,
      is_spend: dataColumnPartial.is_spend ?? true,
      column_type: dataColumnPartial.column_type ?? "unknown",
      is_calendar_of_events: dataColumnPartial.is_calendar_of_events ?? false,
      mta_breakdowns: dataColumnPartial.mta_breakdowns || [],
      target_typical_value: dataColumnPartial.target_typical_value ?? null,
      target_price_value_per_unit_target:
        dataColumnPartial.target_price_value_per_unit_target ?? null,
    };
  };

  const renderSpendConstraintLabel = (dataColumnId: string) => {
    const dataColumn = dataColumnsMap.get(dataColumnId);
    if (dataColumn) {
      return <SpendConstraintLabel dataColumn={dataColumn} />;
    } else {
      const spendConstraintFeature = existingSpendConstraints.find(
        (constraint) => String(constraint.id) === dataColumnId,
      )?.feature;
      const spendConstraintDataColumn = spendConstraintFeature
        ? constructUnknownDataColumn(spendConstraintFeature)
        : constructUnknownDataColumn({ id: String(dataColumnId) });
      return <SpendConstraintLabel dataColumn={spendConstraintDataColumn} />;
    }
  };

  const renderBottomComponent = () => {
    if (isUnavailable) {
      return (
        <div className="flex justify-between items-center w-full bg-mars-2 p-2 text-sm rounded-md">
          <div className="flex justify-start gap-2 items-center">
            <i className="fa-sharp fa-solid fa-circle-info text-mars"></i>
            <span>Spend channel no longer available</span>
          </div>
          <div>
            <TertiaryButton
              className="uppercase text-mars text-sm hover:bg-v2-primary hover:text-mars+1"
              onClick={() => remove(index)}
            >
              Delete constraint
            </TertiaryButton>
          </div>
        </div>
      );
    }

    if (isOff) {
      return (
        <div className="mt-2">
          <Notification
            mainText="Excluded from budget recommendations"
            subText=""
            type={NotificationType.WARNING}
            PillComponent={null}
            RightSideComponent={
              <span className="text-xs text-center pb-2">
                Total spend ={" "}
                {getCurrencySymbol(user.currencies.currentCurrency)}0
              </span>
            }
            size="sm"
            disableBorder
          />
        </div>
      );
    }

    return (
      <div className="flex justify-between items-center py-4">
        <span className="text-xs uppercase text-gray+2">Avg daily spend</span>
        <div className="flex justify-end items-center gap-1">
          <span className="inline-block w-32 pr-2 text-xs text-gray+2 text-right">
            {getDailySpendText(minBudgetValue, false)}
          </span>
          <span className="inline-block text-xs px-2 text-gray+2">to</span>
          <span className="inline-block w-[124px] pr-2 text-xs text-gray+2 text-right">
            {getDailySpendText(maxBudgetValue, true)}
          </span>
        </div>
      </div>
    );
  };

  const handleToggleSpendConstraint = () => {
    if (isUnavailable) {
      return;
    }
    setIsOff((prev) => {
      const nextVal = !prev;
      if (!nextVal) {
        update(index, {
          budget_low: null,
          budget_high: null,
          data_column_id: spendConstraintField.data_column_id,
        });
      }
      return nextVal;
    });
  };

  return (
    <section
      key={spendConstraintField.id}
      className="flex flex-col bg-v2-primary border border-v2-secondary p-6 rounded-sm"
    >
      <div
        className={clsx("pb-2", {
          "border-b border-v2-secondary": !isUnavailable,
        })}
      >
        <div className={clsx("pb-2 flex justify-between items-center")}>
          <div className="flex gap-2 justify-start items-center">
            <Tooltip
              noInfoCircle
              placement="top"
              content={
                isOff
                  ? "Excluded from recommendations"
                  : "Included in recommendations"
              }
            >
              <LightSwitch
                size="small"
                isSet={!isOff && !isUnavailable}
                onChange={handleToggleSpendConstraint}
                id={`constraint:${index}`}
                disabled={false}
              />
            </Tooltip>
            <Controller
              control={form.control}
              name={`spend_constraints.${index}.data_column_id`}
              render={({ field: { value } }) => (
                <>
                  <TextInput disabled hidden value={value} className="hidden" />
                  {renderSpendConstraintLabel(value)}
                </>
              )}
            />
            <Tooltip
              noInfoCircle
              placement="top"
              content={
                <div className="w-[12rem]">Revert to default constraints</div>
              }
            >
              <span
                className={clsx(
                  "text-mars text-xs w-7 h-7 flex justify-center items-center hover:text-mars+1",
                  "hover:bg-[#FDEFEB] text-center cursor-pointer rounded-lg",
                )}
                onClick={() => remove(index)}
              >
                <i className="fa-regular fa-circle-x"></i>
              </span>
            </Tooltip>
          </div>
          <div className="flex justify-end items-start flex-grow">
            <Controller
              control={form.control}
              name={`spend_constraints.${index}.budget_low`}
              render={({ field: { value, onChange } }) => (
                <CurrencyInput
                  placeholder="Default"
                  code={user.currencies.currentCurrency}
                  value={isOff ? "0" : value ?? undefined}
                  onChange={onChange}
                  disabled={isOff || isUnavailable}
                  className={clsx("w-32", {
                    "text-gray+1 outline-none": isUnavailable || isOff,
                    "bg-interstellar-3": isOff,
                  })}
                  inputClassName="text-right"
                />
              )}
            />
            <span className="text-xs px-2 text-gray+2 pt-2">to</span>
            <div className="flex flex-col gap-0 justify-start items-start">
              <Controller
                control={form.control}
                name={`spend_constraints.${index}.budget_high`}
                render={({ field: { value, onChange } }) => (
                  <CurrencyInput
                    code={user.currencies.currentCurrency}
                    placeholder="Default"
                    value={isOff ? "0" : value ?? undefined}
                    onChange={onChange}
                    disabled={isOff || isUnavailable}
                    className={clsx("w-32", {
                      "text-gray+1 outline-none": isUnavailable || isOff,
                      "bg-interstellar-3": isOff,
                    })}
                    inputClassName={clsx("text-right", {
                      "border-mars+1 focus:shadow-[0_0_0_1px_#f05f33]":
                        hasError,
                    })}
                  />
                )}
              />
              {hasError && (
                <span className="text-xxs text-mars mt-0.5">
                  *less than minimum
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-gray">
          Last period's spend:{" "}
          {MetricFormatters.currency(
            previousPeriodSpend ?? 0,
            user.currencies.currentCurrency,
            {
              precision: "exact",
            },
          )}
        </div>
      </div>
      {renderBottomComponent()}
    </section>
  );
}
